import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { PersonRelationshipViewModel } from '../../../models/Entities/Person/Relationship/PersonRelationshipModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonRelationshipFormComponent from './PersonRelationship.Form.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ComponentFunctions from './PersonRelationships.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const PersonRelationshipsListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (props.PersonId) props.GetPersonRelationships(props.PersonId);
  }, [props.PersonId]);

  const handleOpenCreate = () => {
    setSelectedRelationshipId(undefined);
    openForm();
  };

  const handleEdit = (relationshipId: number) => {
    setSelectedRelationshipId(relationshipId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedRelationshipId(undefined);
    dismissForm();
  };

  return (
    <>
      <div className="ComponentHeaderControls">
        <PrimaryButton
          text={t('persons.relationships.list.add', { defaultValue: 'Agregar relación' })}
          iconProps={AddIcon}
          onClick={handleOpenCreate}
          className="actionButton"
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.relationships.list.error', { defaultValue: 'No fue posible obtener las relaciones' })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && props.relationships.length === 0 && (
        <MessageComponent
          message={t('persons.relationships.list.empty', { defaultValue: 'No hay relaciones registradas' })}
          type={MessageBarType.info}
        />
      )}

      {props.relationships.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.relationships.list.relatedPerson', { defaultValue: 'Persona relacionada' })}</th>
              <th>{t('persons.relationships.list.relationshipType', { defaultValue: 'Tipo de relación' })}</th>
              <th>{t('persons.relationships.list.startDate', { defaultValue: 'Inicio' })}</th>
              <th>{t('persons.relationships.list.endDate', { defaultValue: 'Fin' })}</th>
              <th>{t('persons.relationships.list.isActive', { defaultValue: 'Activa' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.relationships.map((item: PersonRelationshipViewModel) => (
              <tr key={item.id} className="TableBodyRow">
                <td className="primary">
                  <Label className="upper">{CommonFunctions.GetPersonDisplayName(item.relatedPerson)}</Label>
                </td>
                <td>
                  <Text>{item.relationshipType?.name || '-'}</Text>
                </td>
                <td>
                  <Text>{CommonFunctions.FormatDate(item.startDate)}</Text>
                </td>
                <td>
                  <Text>{CommonFunctions.FormatDate(item.endDate)}</Text>
                </td>
                <td>
                  <Text>
                    {item.isActive
                      ? t('persons.common.yes', { defaultValue: 'Sí' })
                      : t('persons.common.no', { defaultValue: 'No' })}
                  </Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(item.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`PersonRelationshipDeleteButton-${item.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.relationships.list.deleteTitle', { defaultValue: 'Eliminar relación' })}
                      Description={t('persons.relationships.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar la relación seleccionada?'
                      })}
                      ElementTextHeader={t('persons.relationships.list.relatedPerson', {
                        defaultValue: 'Persona relacionada'
                      })}
                      ElementTextInformation={CommonFunctions.GetPersonDisplayName(item.relatedPerson)}
                      onConfirmation={() => props.DeletePersonRelationship(item.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.relationships.list.loading', { defaultValue: 'Cargando relaciones...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <PersonRelationshipFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        PersonId={props.PersonId}
        PersonType={props.PersonType}
        relationshipId={selectedRelationshipId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PersonRelationshipsListComponent as any);
